import { DeliveryServiceabilityStatus } from '@/lib/enums/DeliveryServiceabilityStatus'
import type { SalesOrder } from '@/lib/services/sales-orders/sales-orders.types'
import { parseCurrency, type NumericInput } from '@/lib/utils'
import type { FsnNetworkIntegration } from './fsn-network.types'

/** `DeliveryServiceabilityPolicyService::WEBSITE_OUTSIDE_AREA_FEE_SOURCE`. */
export const WEBSITE_OUTSIDE_AREA_FEE_SOURCE = 'website_outside_area'

/** Orders received from these systems follow the provider's workflow, never an outgoing FSN send. */
const PROVIDER_SOURCE_SYSTEMS: ReadonlySet<string> = new Set(['fsn_network', 'condolences'])

type FsnOutsideAreaOrder = Partial<
  Pick<SalesOrder, 'delivery_fee_source' | 'delivery_serviceability_status'>
> & {
  order_source_link?: Pick<NonNullable<SalesOrder['order_source_link']>, 'source_system'> | null
}

/**
 * The integration payload, or the smaller summary embedded in an order, which has no
 * `pos_outside_area_enabled`. `enabled` is its legacy alias.
 */
type FsnOutsideAreaSettings = Partial<
  Pick<
    FsnNetworkIntegration,
    'connected' | 'enabled' | 'pos_outside_area_enabled' | 'website_outside_area_enabled'
  >
>

/** A cart or order line, read only for the product value FSN orders need. */
export interface FsnValuedLineItem {
  quantity?: number | string | null
  /** Decimal string. */
  price?: NumericInput
  variant_details?: { price?: NumericInput } | null
}

type FsnValuedLineItems = readonly (FsnValuedLineItem | null | undefined)[] | null | undefined

export function parseOptionalFsnMoney(value: NumericInput): number | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  return parseCurrency(value)
}

export function hasPositiveFsnLineItemValue(items: FsnValuedLineItems = []): boolean {
  return (items ?? []).some((item) => {
    const quantity = Math.max(1, Number(item?.quantity ?? 1) || 1)
    const price = parseCurrency(item?.price ?? item?.variant_details?.price ?? 0)

    return price * quantity > 0
  })
}

export function hasPositiveFsnCartProductValue(
  cart: { cart_items?: FsnValuedLineItems } | null | undefined = {}
): boolean {
  return hasPositiveFsnLineItemValue(cart?.cart_items ?? [])
}

/** The send preview's product eligibility is decided by the API's FSN owner. */
export function hasPositiveFsnOrderProductValue(
  order:
    | {
        fsn_network_order_preview?: Partial<
          NonNullable<SalesOrder['fsn_network_order_preview']>
        > | null
      }
    | null
    | undefined = {}
): boolean {
  return order?.fsn_network_order_preview?.has_positive_product_value === true
}

/**
 * Whether the shop's FSN settings let staff keep an out-of-area address on this order and send it
 * through Flower Shop Network. Mirrors
 * `DeliveryServiceabilityPolicyService::canSubmitFsnNetworkForSalesOrder()`, which the API enforces:
 * the POS setting covers every order the shop took itself, and the website setting covers orders
 * shoppers placed as out-of-area.
 */
export function canSendOrderThroughFsnNetwork(
  order: FsnOutsideAreaOrder | null | undefined,
  settings: FsnOutsideAreaSettings | null | undefined
): boolean {
  if (!order || !settings?.connected) {
    return false
  }

  const sourceSystem = order.order_source_link?.source_system

  if (
    (settings.pos_outside_area_enabled ?? settings.enabled) &&
    !(sourceSystem && PROVIDER_SOURCE_SYSTEMS.has(sourceSystem))
  ) {
    return true
  }

  return Boolean(
    settings.website_outside_area_enabled &&
    order.delivery_fee_source === WEBSITE_OUTSIDE_AREA_FEE_SOURCE &&
    order.delivery_serviceability_status === DeliveryServiceabilityStatus.OutsideDeliveryArea.Value
  )
}
